import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  BreakdownLineItemWithSubItems,
  BreakdownSubItem,
  BreakdownSubItemInput,
} from '@jobos/shared';
import { projectTypeToSlotKey } from '@jobos/shared';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useBreakdown, usePatchBreakdown } from '@/hooks/useBreakdown';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useHistory } from '@/hooks/useHistory';
import { useBreakdownChrome } from '@/hooks/useBreakdownChrome';
import { useSingleCategorySave } from './useSingleCategorySave';
import { displayFromInputs, inputsFromSubItems } from '../carpet/carpet-helpers';
import { useQueryClient } from '@tanstack/react-query';
import { downloadResponseAsFile } from '@/lib/utils';
import { useRegisterBreakdownFlush } from '@/contexts/BreakdownFlushContext';
import { apiClient, APIError } from '@/api/client';
import { SaveStatusIndicator } from '@/components/save/SaveStatusIndicator';
import { SaveErrorBanner } from '@/components/save/SaveErrorBanner';
import { UndoRedoButtons } from '@/components/breakdown-chrome/UndoRedoButtons';
import { RecoverEditsDialog } from '@/components/breakdown-chrome/RecoverEditsDialog';
import { VersionSwitcher } from './VersionSwitcher';
import { DocumentBanner } from './DocumentBanner';
import { RenameDocumentDialog } from './RenameDocumentDialog';
import {
  useBreakdownDocuments,
  useWriteBreakdownDocumentContents,
} from '@/hooks/useBreakdownDocuments';
import type { SingleCategoryConfig } from './singleCategoryConfigs';

interface SingleCategoryBreakdownViewProps {
  jobId: string;
  jobCode?: string | null;
  jobProjectType?: string;
  onDirtyChange?: (dirty: boolean) => void;
  documentId?: string;
  config: SingleCategoryConfig;
}

const EMPTY_SUB_ITEMS: BreakdownSubItem[] = [];

// The single configured-category loose line item for this slot (no projectId).
function findLineItem(
  lineItems: BreakdownLineItemWithSubItems[],
  category: string,
): BreakdownLineItemWithSubItems | null {
  return lineItems.find((li) => li.category === category && !li.projectId) ?? null;
}

const cloneInputs = (items: BreakdownSubItemInput[]): BreakdownSubItemInput[] =>
  items.map((s) => ({ ...s }));

export function SingleCategoryBreakdownView({
  jobId,
  jobCode,
  jobProjectType,
  onDirtyChange,
  documentId,
  config,
}: SingleCategoryBreakdownViewProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const breakdownQuery = useBreakdown(jobId, documentId);
  const catalogQuery = config.useCatalog();
  const ensureLineItem = config.useEnsureLineItem();
  const patchBreakdown = usePatchBreakdown(jobId);
  const isOnline = useOnlineStatus();
  const writeDocContents = useWriteBreakdownDocumentContents(jobId);
  // Slot this surface is mounted for — must match the slot the backend derives
  // from the job's projectType, so the document-list cache is scoped per slot.
  const slotKey = projectTypeToSlotKey(jobProjectType);

  // Preview mode is gone on the doc model — kept as a constant false so the
  // chrome hook + flush registration (which still take/return the field) and
  // the undo/redo guards keep type-checking without churn.
  const isPreviewMode = false;

  const [generating, setGenerating] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);

  // ?docId navigation handle for the in-editor VersionSwitcher. Only meaningful
  // when documentId is set (flag-on path).
  const [, setSearchParams] = useSearchParams();
  const navigateToDoc = useCallback(
    (docId: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (docId) next.set('docId', docId);
        else next.delete('docId');
        return next;
      });
    },
    [setSearchParams],
  );

  // Document metadata for the banner (name + Active marker). Cheap cache hit —
  // the wrapper already populated the list query.
  const { data: documentsForBanner } = useBreakdownDocuments(
    documentId ? jobId : undefined,
    slotKey,
  );
  const currentDocSummary = documentId
    ? documentsForBanner?.find((d) => d.id === documentId)
    : undefined;
  // Excel generation reads the slot's Active doc server-side, so gate it (and
  // the actions menu) to the Active doc. Legacy (flag-off) path has no
  // documentId — treat as always enabled.
  const isActiveDoc = !documentId || currentDocSummary?.isActive === true;
  // When an undo/redo forces an editor reseed, this override replaces the
  // committed lineItem subItems so the editor's `initialKey` memo detects a
  // change and re-seeds its internal state. Cleared whenever the server's
  // committed subItems change.
  const [displayOverride, setDisplayOverride] = useState<BreakdownSubItem[] | null>(null);

  const lineItem = useMemo(
    () =>
      breakdownQuery.data
        ? findLineItem(breakdownQuery.data.lineItems, config.category)
        : null,
    [breakdownQuery.data, config.category],
  );

  // Stable identity for the per-document rates so useSingleCategorySave's saveFn
  // (and the autosave effects keyed on it) aren't recreated on every render.
  const rates = useMemo(
    () => ({
      taxRate: breakdownQuery.data?.taxRate ?? 0,
      markupRate: breakdownQuery.data?.markupRate ?? 0,
      roundingPrecision: breakdownQuery.data?.roundingPrecision ?? null,
    }),
    [
      breakdownQuery.data?.taxRate,
      breakdownQuery.data?.markupRate,
      breakdownQuery.data?.roundingPrecision,
    ],
  );

  const {
    pendingSubItems,
    setPending,
    isDirty,
    isSaving,
    hasOfflineEdits,
    saveError,
    save,
    flushPendingSave,
    downloadBackup,
    pendingRecovery,
    acceptRecovery,
    discardRecovery,
  } = useSingleCategorySave({
    jobId,
    lineItem,
    ensureLineItem,
    patchBreakdown,
    writeDocContents,
    queryClient,
    isOnline,
    rates,
    documentId,
    category: config.category,
    offlineKey: config.offlineKey,
    backupKind: config.backupKind,
  });

  const history = useHistory<BreakdownSubItemInput[]>({ clone: cloneInputs });

  // Reset editor override whenever the committed subItems change (after save
  // or refetch). Without this, the editor would keep showing the last undo
  // snapshot after a fresh save lands.
  useEffect(() => {
    setDisplayOverride(null);
  }, [lineItem]);

  // "Current" state for history operations — prefer pending edits over the
  // committed server state. Used as the "before" snapshot for markDirty and
  // as the state pushed onto the redo stack when undoing.
  const currentInputs = useMemo<BreakdownSubItemInput[]>(
    () => pendingSubItems ?? inputsFromSubItems(lineItem?.subItems ?? []),
    [pendingSubItems, lineItem],
  );

  const handleEditorChange = useCallback(
    (next: BreakdownSubItemInput[]) => {
      // Capture the pre-change snapshot so undo can restore it. The history
      // hook debounces rapid calls, so only the first markDirty in a burst
      // actually snapshots.
      history.markDirty(currentInputs);
      setPending(next);
    },
    [currentInputs, history, setPending],
  );

  const applyRestoredInputs = useCallback(
    (restored: BreakdownSubItemInput[]) => {
      setDisplayOverride(displayFromInputs(restored));
      setPending(restored);
    },
    [setPending],
  );

  const handleUndo = useCallback(() => {
    if (isPreviewMode) return;
    const prev = history.undo(currentInputs);
    if (prev) applyRestoredInputs(prev);
  }, [applyRestoredInputs, currentInputs, history, isPreviewMode]);

  const handleRedo = useCallback(() => {
    if (isPreviewMode) return;
    const next = history.redo(currentInputs);
    if (next) applyRestoredInputs(next);
  }, [applyRestoredInputs, currentInputs, history, isPreviewMode]);

  useBreakdownChrome({
    isDirty,
    isSaving,
    isPreviewMode,
    onSave: () => void save(),
    onUndo: handleUndo,
    onRedo: handleRedo,
    onDirtyChange,
  });

  // Register a flush handle so JobDetail's leave paths (tab switch, back, and
  // project-type switch) persist pending edits to the server before navigating.
  // flushPendingSave saves when dirty and no-ops when clean — autosave-on-leave.
  useRegisterBreakdownFlush(
    () => ({
      flush: flushPendingSave,
    }),
    [flushPendingSave],
  );

  if (breakdownQuery.isLoading || catalogQuery.isLoading) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto p-6 text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (breakdownQuery.isError) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto p-6 text-sm text-red-600">
        Failed to load breakdown.
      </div>
    );
  }
  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto p-6 text-sm text-red-600">
        Failed to load {config.catalogErrorLabel}.
      </div>
    );
  }

  const committedSubItems = lineItem?.subItems ?? EMPTY_SUB_ITEMS;
  // Priority for what the editor displays:
  //   1. undo/redo override (briefly, until next commit)
  //   2. committed server state
  const editorInitialSubItems = displayOverride ?? committedSubItems;
  const hasContent = committedSubItems.length > 0 || isDirty;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Persist any in-flight or pending edits before hitting the backend —
      // otherwise the generator reads a stale DB and the sheet comes out empty.
      await flushPendingSave();

      // Route through apiClient so the request hits VITE_API_URL (Railway in
      // prod). Raw fetch('/api/...') only worked in dev because Vite proxies
      // /api to localhost:3000 — the prod Vercel deploy has no such rewrite,
      // so the same path 404s against the static origin.
      const res = await apiClient.post<Response>(config.generate.path(jobId));
      await downloadResponseAsFile(res, config.generate.filename(jobCode ?? 'job'));
    } catch (err) {
      if (err instanceof APIError && err.status === 422) {
        const message = typeof err.data.error === 'string' ? err.data.error : 'Too many items.';
        toast({
          title: 'Too many items',
          description: message,
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Failed to generate',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="p-4 md:p-6 max-w-5xl mx-auto">
        {saveError && (
          <SaveErrorBanner
            error={saveError}
            isSaving={isSaving}
            onRetry={() => void save()}
            onDownloadBackup={() => downloadBackup(jobCode ?? 'job')}
          />
        )}
        {/* Document banner (flag-on path only) */}
        {documentId && currentDocSummary && (
          <div className="mb-3">
            <DocumentBanner
              name={currentDocSummary.name}
              isActive={currentDocSummary.isActive}
              onRename={() => setRenameDialogOpen(true)}
            />
          </div>
        )}
        <div className="flex items-center justify-between gap-3 mb-3 text-sm">
          <UndoRedoButtons
            canUndo={history.canUndo}
            canRedo={history.canRedo}
            onUndo={handleUndo}
            onRedo={handleRedo}
          />
          <div className="flex items-center gap-3">
            <SaveStatusIndicator
              isOnline={isOnline}
              isSaving={isSaving}
              isDirty={isDirty}
              hasOfflineEdits={hasOfflineEdits}
              hasContent={hasContent}
              onSave={() => void save()}
            />
            {documentId && (
              <VersionSwitcher
                jobId={jobId}
                slotKey={slotKey}
                documentId={documentId}
                onNavigate={navigateToDoc}
                disabled={isSaving || isDirty}
              />
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerate}
              disabled={generating || !isActiveDoc}
            >
              {generating ? 'Generating…' : 'Generate Excel'}
            </Button>
          </div>
        </div>
        {config.renderEditor(catalogQuery.data, {
          initialSubItems: editorInitialSubItems,
          onChange: handleEditorChange,
          readOnly: isPreviewMode,
        })}
      </div>

      <RecoverEditsDialog
        open={pendingRecovery !== null}
        onRecover={acceptRecovery}
        onDiscard={discardRecovery}
      />

      {documentId && (
        <RenameDocumentDialog
          open={renameDialogOpen}
          onOpenChange={setRenameDialogOpen}
          jobId={jobId}
          document={currentDocSummary ?? null}
        />
      )}
    </div>
  );
}
